import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Board } from "../lib/api/boards";
import BoardContextMenu from "./BoardContextMenu";

interface BoardListItemProps {
  board: Board;
  onRename: (id: string, newName: string) => void;
  onDelete: (id: string) => void;
}

const BoardListItem = ({
  board,
  onRename,
  onDelete,
}: BoardListItemProps) => {
  const { t } = useTranslation("common");
  const navigate = useNavigate();

  const handleClick = () => {
    navigate(`/boards/${board.id}`, { state: { boardName: board.name } });
  };

  // Format date for display
  const formatDate = (dateString: string) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "";

    const now = new Date();
    const diffTime = Math.abs(now.getTime() - date.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 1) return t("time.today");
    if (diffDays === 2) return t("time.yesterday");
    if (diffDays <= 7) return t("time.daysAgo", { count: diffDays - 1 });
    return date.toLocaleDateString();
  };

  // All boards belong to the current user (already filtered by userId in backend)
  const getOwnerName = () => t("boards.you");

  const BoardIcon = () => (
    <div className="w-8 h-8 bg-neutral-100 dark:bg-neutral-800 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
      <span className="w-4 h-4 text-accent-primary" style={{maskImage: `url(https://cdn.dashbal.com/assets/icon_boards_lamp.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_boards_lamp.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block'}} role='img' />
    </div>
  );

  return (
    <div
      id="onboarding-board-content"
      onClick={handleClick}
      className="group relative bg-transparent hover:bg-neutral-50 dark:hover:bg-neutral-800/30 transition-all cursor-pointer border-b border-neutral-100 dark:border-neutral-800/50 last:border-b-0"
    >
      {/* Desktop/Tablet view */}
      <div className="hidden md:flex items-center py-3 px-4">
        <div className="flex items-center space-x-3.5 flex-1 min-w-0">
          <BoardIcon />
          <div className="min-w-0 flex-1">
            <h3 className="text-sm font-semibold text-text-primary dark:text-neutral-200 truncate group-hover:text-accent-primary transition-colors">
              {board.name}
            </h3>
          </div>
        </div>

        <div className="flex-shrink-0 w-32 px-3">
          <span className="text-[11px] text-neutral-500 dark:text-neutral-400">
            {formatDate(board.updatedAt)}
          </span>
        </div>

        <div className="hidden lg:block flex-shrink-0 w-32 px-3">
          <span className="text-[11px] text-neutral-500 dark:text-neutral-400">
            {getOwnerName()}
          </span>
        </div>

        <div className="flex-shrink-0" onClick={(e) => e.stopPropagation()}>
          <BoardContextMenu
            boardId={board.id}
            boardName={board.name}
            onRename={onRename}
            onDelete={onDelete}
          />
        </div>
      </div>

      {/* Mobile view */}
      <div className="md:hidden p-4">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center space-x-3.5 flex-1 min-w-0">
            <BoardIcon />
            <div className="min-w-0 flex-1">
              <h3 className="text-sm font-semibold text-text-primary dark:text-neutral-200 truncate group-hover:text-accent-primary transition-colors">
                {board.name}
              </h3>
              <div className="flex items-center gap-2 text-[10px] text-neutral-500 dark:text-neutral-400 mt-0.5">
                <span>{formatDate(board.updatedAt)}</span>
                <span>•</span>
                <span>{getOwnerName()}</span>
              </div>
            </div>
          </div>
          <div className="flex-shrink-0" onClick={(e) => e.stopPropagation()}>
            <BoardContextMenu
              boardId={board.id}
              boardName={board.name}
              onRename={onRename}
              onDelete={onDelete}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default BoardListItem;
